import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
} from "@mui/material";
import DynamicTable from "../../../../../components/Dynamic-table";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  printDischargeReport,
  waitForPrintNodeAssets,
} from "./printDischargeReport";

const NO_STORE_HEADERS = {
  "Cache-Control": "no-cache, no-store, must-revalidate",
  Pragma: "no-cache",
  Expires: "0",
} as const;

/**
 * Format diagnosis for print/display as comma-separated names.
 * Expects string[] (or a single string name). Does not split names on commas.
 */
function formatDiagnosisList(value: unknown): string {
  if (value == null || value === "") return "";

  const recover = (text: string): string => {
    const trimmed = text.trim();
    if (!trimmed) return "";
    const parts = trimmed
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length > 1 && parts.every((p) => p.length === 1)) {
      return parts.join("");
    }
    return trimmed;
  };

  if (typeof value === "string") {
    return recover(value);
  }

  if (Array.isArray(value)) {
    if (
      value.length > 1 &&
      value.every((item) => typeof item === "string" && item.length === 1)
    ) {
      return value.join("");
    }
    const names = value
      .map((item) => {
        if (typeof item === "string") return recover(item);
        if (item && typeof item === "object") {
          return recover(
            String(
              (item as any).diagnosisName ||
                (item as any).name ||
                (item as any).label ||
                ""
            )
          );
        }
        return "";
      })
      .filter(Boolean);
    return Array.from(new Set(names)).join(", ");
  }

  return String(value);
}

dayjs.extend(customParseFormat);

interface Form {
  dischargeData?: any;
  examinationData?: string;
  detailedExaminationNotes?: string;
  referal?: boolean;
  allData?: any;
  formName?: string;
  id?: string;
  report?: boolean;
  directPrint?: boolean;
  handleClose?: () => void;
  formHeader?: any;
  routsAdm?: any;
  embedded?: boolean;
}

const DischargeReport: React.FC<Form> = ({
  formHeader,
  dischargeData,
  detailedExaminationNotes,
  referal,
  allData,
  id,
  formName,
  directPrint,
  routsAdm,
  handleClose,
  embedded = false,
}) => {
  const projectProfile = useSelector(
    (state: any) => state?.projectProfileSlice?.projectProfile
  );
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const loginper = profile?.data?.fullName || profile?.data?.firstName || ''

  const DischargeReportRef = useRef<HTMLDivElement>(null);
  const printedRef = useRef(false);
  // For direct print, start empty so we never preview until the form API returns
  const [dischargedData, setDischargedData] = useState<any>(
    directPrint ? {} : dischargeData || {}
  );
  const [printDataReady, setPrintDataReady] = useState(!directPrint);
  const [headerReady, setHeaderReady] = useState(!directPrint);
  const [routesReady, setRoutesReady] = useState(!directPrint);
  const [headerData, setHeaderData] = useState<any>(null);
  const [logoLoadingError, setLogoLoadingError] = useState(false);
  const [Data, setData] = useState<any>(allData || {});
  const [routeAdm, setRouteAdm] = useState<any>(routsAdm);

  const getRouteAdm = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_OPD_API_URL}/routes-of-administration/getAllByPagination`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...NO_STORE_HEADERS,
          },
          params: {
            count: 50000,
            pageNo: 1,
            _: Date.now(),
          },
        }
      );
      const routes = response.data?.data;
      setRouteAdm(routes);
    } catch (error) {
      toast.error("Failed to fetch routes of administration");
    } finally {
      setRoutesReady(true);
    }
  };

  const fetchHeaderFormData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/cms/get_prescription_header_and_footer`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...NO_STORE_HEADERS,
          },
          params: {
            userId: id,
            location: "default",
            _: Date.now(),
          },
        }
      );
      const fetchedData = response?.data?.data;
      setHeaderData(fetchedData);
    } catch (error) {
      toast.error("Failed to fetch form header data.");
    } finally {
      setHeaderReady(true);
    }
  };

  const fetchFormData = async (formName: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/getFormDataBySearchId/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...NO_STORE_HEADERS,
          },
          params: {
            formName,
            _: Date.now(),
          },
        }
      );
      const payload = response?.data?.data;
      // Backend maps patientData by patientId on getFormDataBySearchId
      const patient = payload?.patientData || dischargeData?.patientData || {};
      setData({
        ...(payload || {}),
        patientData: patient,
      });
      const form = payload?.formData || {};
      const fetchedData = {
        ...form,
        admissionType: payload?.admissionType || form?.admissionType || "N/A",
        // Prefer form / backend-mapped patient; then admission row prop
        patientName:
          patient?.patientName ||
          payload?.patientName ||
          form?.patientName ||
          dischargeData?.patientName ||
          dischargeData?.patientData?.patientName ||
          "",
        mrn: (() => {
          const candidates = [
            patient?.mrn,
            payload?.mrn,
            dischargeData?.mrn,
            dischargeData?.patientData?.mrn,
            form?.mrn,
          ];
          for (const value of candidates) {
            if (value == null || value === "" || value === 0 || value === "0") {
              continue;
            }
            return value;
          }
          return "";
        })(),
        guardiansName:
          form?.guardiansName ||
          patient?.guardiansName ||
          dischargeData?.guardiansName ||
          "",
        gender:
          form?.gender ||
          patient?.gender ||
          dischargeData?.patientData?.gender ||
          dischargeData?.gender ||
          "",
        doctorName:
          form?.doctorName ||
          payload?.doctorName ||
          dischargeData?.doctorName ||
          "",
        admissionDateAndTime:
          form?.admissionDateAndTime ||
          form?.dateOfAdmission ||
          payload?.admissionDateAndTime ||
          dischargeData?.admissionDateAndTime,
        medicines: form?.medicines || form?.medication || [],
        patientData: patient,
      };

      setDischargedData(fetchedData);
    } catch (error) {
      toast.error("Failed to fetch form data.");
    } finally {
      setPrintDataReady(true);
    }
  };

  useEffect(() => {
    getRouteAdm();
    if (id && formName) {
      fetchHeaderFormData();
      fetchFormData(formName);
      return;
    }
    // Without an admission id we cannot load the form — still unblock so UI can recover
    if (directPrint) {
      setPrintDataReady(true);
      setHeaderReady(true);
    }
  }, [id, formName]);

  const handleLogoError = () => {
    setLogoLoadingError(true);
  };

  function formatAge(dob: any) {
    if (!dob) return "";

    const dobDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - dobDate.getFullYear();
    let months = today.getMonth() - dobDate.getMonth();
    let days = today.getDate() - dobDate.getDate();

    if (months < 0 || (months === 0 && days < 0)) {
      years--;
      months += 12;
    }

    if (days < 0) {
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
      months--;
    }

    if (years > 0) {
      return `${years}Y${months > 0 ? ` ${months}M` : ""}`;
    } else if (months > 0) {
      return `${months}M`;
    } else {
      return `${days}D`;
    }
  }

  const defaultData = {
    admissionType:
      dischargedData?.admissionType || allData?.admissionType || allData?.formData?.admissionType || allData?.patientData?.admissionType || "N/A",
    patientName: dischargedData?.patientName || "",
    guardianName: dischargedData?.guardiansName || "",
    dob: dischargedData?.dob ? formatAge(dischargedData?.dob) : "",
    age: dischargedData?.age || "",
    sex: dischargedData?.sex || "",
    weight: dischargedData?.weight || "",
    mrn: dischargedData?.mrn || "",
    admissionDate: dischargedData?.admissionDateAndTime
      ? dayjs(dischargedData?.admissionDateAndTime)?.format(
        "DD.MM.YYYY hh:mm A"
      )
      : "",
    dischargeDateTime: dischargedData?.dateOfDischarge
      ? dayjs(dischargedData?.dateOfDischarge)?.format("DD.MM.YYYY hh:mma")
      : "",
    presentingComplaint:
      typeof dischargedData?.presentingComplaints === "string"
        ? dischargedData?.presentingComplaints
        : Array.isArray(dischargedData?.presentingComplaints)
          ? dischargedData?.presentingComplaints
            ?.map((item: any) =>
              typeof item === "object" && item !== null ? item.complaint : item
            )
            ?.join(", ")
          : dischargedData?.presentingComplaints &&
            typeof dischargedData?.presentingComplaints === "object"
            ? Object.keys(dischargedData?.presentingComplaints)
              ?.map(
                (key) => `${key}: ${dischargedData?.presentingComplaints[key]}`
              )
              ?.join(", ")
            : "",
    briefHistory: dischargedData?.briefHistory || "",
    examinationData: Array.isArray(dischargedData?.examinations)
      ? dischargedData?.examinations?.join(", ")
      : typeof dischargedData?.examinations === "string"
        ? dischargedData?.examinations
        : dischargedData?.examinations
          ? Object.entries(dischargedData?.examinations)
            ?.map(([key, value]) => `${key}: ${value}`)
            ?.join(", ")
          : "",
    diagnosis: formatDiagnosisList(dischargedData?.diagnosis),
    finalDiagnosis: formatDiagnosisList(dischargedData?.finalDiagnosis),
    SergonName: dischargedData?.surgeonName || "",
    procedureDone: dischargedData?.procedureDone || "",
    operativeFindings: dischargedData?.operativeFindings || ".",
    additionalNotes: dischargedData?.additionalNotes || ".",
    anaesthesia: dischargedData?.anaesthesia || "",
    dischargeAdvisedBy: dischargedData?.dischargeTo || "",
    referedBy: dischargedData?.referedBy || "",
    referTo: dischargedData?.referTo || "",
    referalNote: dischargedData?.referalNote || "",
    resonOfRefer: dischargedData?.resonOfRefer || "",
    lama: dischargedData?.lama,
    dor: dischargedData?.dor,
    conditionOnDischarge: dischargedData?.conditionOnDischarge || "",
    diagnosisSignificant: Array.isArray(dischargedData?.diagnosisSignificant)
      ? dischargedData.diagnosisSignificant?.join(", ")
      : typeof dischargedData?.diagnosisSignificant === "string"
        ? dischargedData.diagnosisSignificant
        : dischargedData?.diagnosisSignificant
          ? Object.entries(dischargedData.diagnosisSignificant)
            ?.map(([key, value]) => `${key}: ${value}`)
            ?.join(", ")
          : "",
    medications: dischargedData?.medicines || [],
    dateAndTime: dischargedData?.dateAndTime,
    department: dischargedData?.department,
    foodInstructions: dischargedData?.foodInstructions,
    orderedBy: dischargedData?.orderedBy,
    doctorName: dischargedData?.doctorName,
  };

  useEffect(() => {
    if (!directPrint || !printDataReady || !headerReady || !routesReady) return;
    if (printedRef.current) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    let retryTimer: number | undefined;

    const waitForPaint = () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

    const isContentReady = (node: HTMLDivElement | null) => {
      if (!node) return false;
      const text = node.textContent?.replace(/\s+/g, " ").trim() || "";
      // Shell labels alone are ~short; require real patient fields after fetch + paint
      const hasPatientData =
        Boolean(dischargedData?.patientName) ||
        Boolean(dischargedData?.mrn) ||
        Boolean(dischargedData?.admissionDateAndTime);
      return hasPatientData && text.length > 80;
    };

    const tryPrint = async () => {
      if (cancelled || printedRef.current) return;
      const node = DischargeReportRef.current;

      if (!isContentReady(node)) {
        attempts += 1;
        if (attempts < maxAttempts) {
          retryTimer = window.setTimeout(tryPrint, 150);
          return;
        }
        toast.error("Print content is not ready. Please try again.");
        handleClose?.();
        return;
      }

      printedRef.current = true;
      try {
        await waitForPaint();
        // Same as after a cache clear: wait until letterhead/images are decoded
        if (DischargeReportRef.current) {
          await waitForPrintNodeAssets(DischargeReportRef.current);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 150));
        if (cancelled || !DischargeReportRef.current) return;
        await printDischargeReport(
          DischargeReportRef.current,
          referal ? "Patient Referral Report" : "Patient Discharge Report"
        );
      } catch (error) {
        console.error(error);
        toast.error("Unable to open the print preview.");
      } finally {
        if (!cancelled) handleClose?.();
      }
    };

    // First attempt after a short settle so React can commit fetched data to the DOM
    retryTimer = window.setTimeout(tryPrint, 100);

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [
    directPrint,
    printDataReady,
    headerReady,
    routesReady,
    referal,
    dischargedData?.patientName,
    dischargedData?.mrn,
    dischargedData?.admissionDateAndTime,
  ]);

  const renderDuration = (duration?: string) => {
    if (!duration) return "";

    const durationMap: { [key: string]: string } = {
      Minutes: "منٹ",
      Minute: "منٹ",
      Hours: "گھنٹہ",
      Hour: "گھنٹہ",
      Days: "دن",
      Day: "دن",
      Weeks: "ہفتہ",
      Week: "ہفتہ",
      Months: "مہینہ",
      Month: "مہینہ",
      Years: "سال",
      Year: "سال",
      Lifetime: "زندگی بھر",
    };

    const text = String(duration).trim();
    const parts = text.split(/\s+/);
    const isNumber = (value: string) => /^\d+(?:[./]\d+)?$/.test(value);
    const hasUrdu = (value: string) => /[\u0600-\u06FF]/.test(value);

    let number = "";
    let unit = text;

    if (parts.length === 2) {
      const [first, second] = parts;
      if (isNumber(first)) {
        number = first;
        unit = durationMap[second] || second;
      } else if (isNumber(second)) {
        number = second;
        unit = durationMap[first] || first;
      }
    } else if (hasUrdu(text)) {
      const match = text.match(/\d+(?:[./]\d+)?/);
      if (match) {
        number = match[0];
        unit = text.replace(match[0], "").replace(/\s+/g, " ").trim();
      }
    }

    if (number && hasUrdu(unit)) {
      return (
        <span className="duration-urdu" dir="ltr">
          <span className="urduNotationFont" dir="rtl" lang="ur">
            {unit}
          </span>
          <span className="duration-number" dir="ltr">
            {number}
          </span>
        </span>
      );
    }

    if (hasUrdu(text)) {
      return (
        <span className="urduNotationFont" dir="rtl" lang="ur">
          {text}
        </span>
      );
    }

    return text;
  };


  return (
    <>
      <Box
        ref={DischargeReportRef}
        className={embedded ? undefined : "discharge-print-root clinical-print"}
        sx={{ display: "flex", flexDirection: "column", height: "auto" }}
      >
        {!embedded && (
        <div
          className="discharge-letterhead"
          style={{
            width: "100%",
            height: "120px",
            overflow: "hidden",
          }}
        >
          {(formHeader?.headerUrl || headerData?.headerUrl) && (
            <img
              src={formHeader?.headerUrl || headerData?.headerUrl}
              alt=""
              style={{
                width: "100%",
                height: "120px",
                objectFit: "fill",
                display: "block",
              }}
            />
          )}
        </div>
        )}
        <Box mb={2}>
          {allData?.patientData && (
            <Box p={2} sx={{ borderBottom: "2px solid #000" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="h6"
                    style={{
                      color: "#000",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    Patient Name :{" "}
                    <span
                      style={{
                        color: "#000",
                        fontSize: "13px",
                        fontWeight: "normal",
                        textTransform: "capitalize",
                      }}
                    >
                      {allData?.patientData?.patientName}
                    </span>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Box display="flex" justifyContent="flex-end">
                    <Typography
                      variant="h6"
                      style={{
                        color: "#000",
                        fontWeight: "bold",
                        fontSize: "13px",
                      }}
                    >
                      MRN # :{" "}
                      <span
                        style={{
                          color: "#000",
                          fontSize: "13px",
                          fontWeight: "normal",
                        }}
                      >
                        {allData?.patientData?.mrn}
                      </span>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        <Box sx={{ padding: "20px" }}>
          <Typography
            variant="h6"
            align="center"
            gutterBottom
            sx={{ fontSize: "18px" }}
          >
            {referal ? (
              "Referral FORM"
            ) : (
              <>
                <Box
                  sx={{
                    textAlign: "center",
                    py: 1,
                    marginTop: "-30px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "inline-block", fontSize: "18px" }}
                  >
                    DISCHARGE FORM
                  </Typography>
                </Box>
              </>
            )}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow
                        sx={{ "& td": { borderBottom: "1px solid #ddd" } }}
                      >
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Patient Name
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.patientName}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          MRN
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.mrn}
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{ "& td": { borderBottom: "1px solid #ddd" } }}
                      >
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          S/o, D/o, W/o
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.guardianName}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Date of Admission & Time
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.admissionDate}
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{ "& td": { borderBottom: "1px solid #ddd" } }}
                      >
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Discharge Date & Time
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                            走向: "Discharge Date & Time",
                          }}
                        >
                          {defaultData?.dischargeDateTime || "N/A"}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Weight
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {dischargedData?.weight
                            ? `${dischargedData?.weight} kg`
                            : ""}
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{ "& td": { borderBottom: "1px solid #ddd" } }}
                      >
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Age/Sex
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.dob ? `${defaultData?.dob} ` : "N/A"} /{" "}
                          {defaultData?.sex || "N/A"}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Admission Type
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.admissionType || ""}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "11px", fontWeight: "600" }}>
                  Presenting Complaint:
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                  {defaultData?.presentingComplaint}
                </Typography>
              </Grid>
              <Divider sx={{ width: "100%", mt: 0.4, mb: 0.4 }} />
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "11px", fontWeight: "600" }}>
                  Brief History & Examination:
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                  {defaultData?.briefHistory}
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                  {defaultData?.examinationData}
                </Typography>
                {detailedExaminationNotes && (
                  <Box mt={1}>
                    <Typography sx={{ fontSize: "11px", fontWeight: "600" }}>
                      Detailed Examination Notes:
                    </Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                      {detailedExaminationNotes}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Divider sx={{ width: "100%", mt: 0.4, mb: 0.4 }} />
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "11px", fontWeight: "600" }}>
                  Diagnostic Investigations:
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                  {defaultData?.diagnosisSignificant}
                </Typography>
              </Grid>
              <Divider sx={{ width: "100%", mt: 0.4, mb: 0.4 }} />
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Diagnosis
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.finalDiagnosis
                            ? defaultData?.finalDiagnosis
                            : defaultData?.diagnosis || "N/A"}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Doctor Name
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #ddd",
                            borderLeft: "1px solid #ddd",
                            borderTop: "1px solid #ddd",
                            fontSize: "11px",
                            fontWeight: "normal",
                          }}
                        >
                          {defaultData?.SergonName || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        {defaultData?.admissionType !== "Medical" && (
                          <>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #ddd",
                                borderLeft: "1px solid #ddd",
                                borderTop: "1px solid #ddd",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              Procedure Done
                            </TableCell>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #ddd",
                                borderLeft: "1px solid #ddd",
                                borderTop: "1px solid #ddd",
                                fontSize: "11px",
                                fontWeight: "normal",
                              }}
                            >
                              {defaultData?.procedureDone
                                ? defaultData?.procedureDone
                                : "N/A"}
                            </TableCell>
                          </>
                        )}
                        {referal ? (
                          <>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #ddd",
                                fontSize: "11px",
                                borderTop: "1px solid #ddd",
                                fontWeight: "600",
                              }}
                            >
                              Condition at time of Refer
                            </TableCell>
                            <TableCell
                              sx={{
                                borderRight: "1px solid #ddd",
                                fontSize: "11px",
                                borderTop: "1px solid #ddd",
                                fontWeight: "normal",
                              }}
                            >
                              {defaultData?.conditionOnDischarge}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            {defaultData?.admissionType !== "Medical" && (
                              <>
                                <TableCell
                                  sx={{
                                    borderRight: "1px solid #ddd",
                                    fontSize: "11px",
                                    borderLeft: "1px solid #ddd",
                                    borderTop: "1px solid #ddd",
                                    fontWeight: "600",
                                  }}
                                >
                                  Anaesthesia
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderRight: "1px solid #ddd",
                                    fontSize: "11px",
                                    borderTop: "1px solid #ddd",
                                    fontWeight: "normal",
                                  }}
                                >
                                  {defaultData?.anaesthesia
                                    ? defaultData?.anaesthesia
                                    : "N/A"}
                                </TableCell>
                              </>
                            )}
                          </>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Divider sx={{ width: "100%", mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {referal ? (
                    <>
                      <Typography sx={{ fontSize: "11px", fontWeight: "600" }}>
                        Referal Notes:
                      </Typography>
                      <Typography sx={{ fontSize: "11px", fontWeight: "normal" }}>
                        {defaultData?.referalNote}
                      </Typography>
                    </>
                  ) : (
                    <>
                      {defaultData?.admissionType !== "Medical" && (
                        <Typography sx={{ fontSize: "11px", lineHeight: 1.4 }}>
                          <span style={{ fontWeight: 700 }}>Operative Findings: </span>
                          <span
                            className="discharge-note-value"
                            style={{ fontSize: "10px", fontWeight: 400 }}
                          >
                            {defaultData?.operativeFindings}
                          </span>
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>

                <Grid item xs={6}>
                  {referal ? (
                    <>
                      <Typography sx={{ fontSize: "11px", lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 700 }}>Additional Notes: </span>
                        <span
                          className="discharge-note-value"
                          style={{ fontSize: "10px", fontWeight: 400 }}
                        >
                          {defaultData?.additionalNotes}
                        </span>
                      </Typography>
                    </>
                  ) : (
                    <>
                      {defaultData?.admissionType !== "Medical" && (
                        <Typography sx={{ fontSize: "11px", lineHeight: 1.4 }}>
                          <span style={{ fontWeight: 700 }}>Additional Notes: </span>
                          <span
                            className="discharge-note-value"
                            style={{ fontSize: "10px", fontWeight: 400 }}
                          >
                            {defaultData?.additionalNotes}
                          </span>
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ width: "100%", mt: 0.4 }} />
              {!referal && (
                <Grid item xs={12} md={12} lg={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: "1px solid #ddd",
                              borderLeft: "1px solid #ddd",
                              borderTop: "1px solid #ddd",
                              fontSize: "11px",
                            }}
                          >
                            <strong>Discharge Advised By:</strong>{" "}
                            {defaultData?.SergonName}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: "1px solid #ddd",
                              borderLeft: "1px solid #ddd",
                              borderTop: "1px solid #ddd",
                              fontSize: "11px",
                            }}
                          >
                            <strong>Condition on Discharge:</strong>{" "}
                            {defaultData?.conditionOnDischarge}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
              <Grid item xs={12}>
                <DynamicTable
                  headers={[
                    "SN",
                    "Medicine",
                    "Dosage",
                    "Route",
                    "Frequency",
                    "Duration",
                  ]}
                  data={defaultData?.medications?.map((item: any, idx: any) => {
                    const route = routeAdm?.find(
                      (route: any) => route?._id === item?.routAdministrationId
                    );
                    const routeAbbreviation = route ? route?.RA_Urdu_Term : "";
                    const frequencyText =
                      item?.frequency?.urdu_notation ||
                      item?.frequency?.math_notation ||
                      item?.frequency?.eng_notation ||
                      item?.frequency?.abr_notation ||
                      "";

                    return [
                      idx + 1,
                      <span>
                        {item?.name} <br />
                        {item?.note}
                      </span>,
                      item?.dosage,
                      <span className="urduNotationFont" dir="rtl" lang="ur">
                        {routeAbbreviation}
                      </span>,
                      <span className="urduNotationFont" dir="rtl" lang="ur">
                        {frequencyText}
                      </span>,
                      renderDuration(item?.duration),
                    ];
                  })}


                />

                <div className="discharge-order-sign">
                  <div className="discharge-order-side">
                    {defaultData?.orderedBy?.trim() && (
                      <div>
                        <span style={{ fontWeight: 600, fontSize: "11px" }}>
                          Ordered By:{" "}
                        </span>
                        <span style={{ fontSize: "11px" }}>
                          {defaultData.orderedBy}
                        </span>
                      </div>
                    )}
                    {!referal &&
                      (defaultData?.department ||
                        defaultData?.dateAndTime ||
                        defaultData?.foodInstructions) && (
                        <div className="discharge-visit-box">
                          <div className="discharge-visit-intro">
                            <span className="discharge-visit-title">Note:</span>
                            Please visit the specified department on the given
                            date for your examination.
                          </div>
                          <div className="discharge-visit-row">
                            <div>
                              <span className="discharge-visit-label">
                                Department:
                              </span>
                              <span>{defaultData?.department || "N/A"}</span>
                            </div>
                            <div>
                              <span className="discharge-visit-label">
                                Next Examination Date:
                              </span>
                              <span>{defaultData?.dateAndTime || "N/A"}</span>
                            </div>
                          </div>
                          <div>
                            <span className="discharge-visit-label">
                              Instructions:
                            </span>
                            <span>{defaultData?.foodInstructions || "N/A"}</span>
                          </div>
                        </div>
                      )}
                  </div>
                  {(Data?.stamp || Data?.signature) && (
                    <div className="discharge-sign-block flex justify-end items-end flex-col">
                      <img
                        src={`${Data?.signature || ""}`}
                        alt=""
                        className="w-24 ml-4"
                      />
                      <img
                        src={Data?.stamp || ""}
                        alt=""
                        className="discharge-sign-stamp w-26 ml-4"
                      />
                    </div>
                  )}
                </div>
              </Grid>
              {referal && (
                <Box px={1}>
                  <Grid container>
                    <Grid item md={12} sm={12} xs={12}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "11px", fontWeight: "600" }}
                        >
                          Refer To
                        </Typography>
                        <Typography
                          sx={{ fontSize: "11px", fontWeight: "normal" }}
                        >
                          {defaultData?.referTo}
                        </Typography>
                      </Box>
                    </Grid>
                    <Divider sx={{ width: "100%", mt: 0.2, mb: 0.3 }} />
                    <Grid item md={12} sm={12} xs={12}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "11px", fontWeight: "600" }}
                        >
                          Reason of Refer
                        </Typography>
                        <Typography
                          sx={{ fontSize: "11px", fontWeight: "normal" }}
                        >
                          {defaultData?.resonOfRefer}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>

          </Box>

        </Box>


        {!embedded && (
        <div className="discharge-page-footer">
          {!formHeader?.stamp && !formHeader?.signature ? (
            <div className="discharge-footer-note">
              Electronically verified Report. No Signature Required. Not Valid for any court of law.
            </div>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {formHeader?.signature && (
                  <Box>
                    <img
                      src={formHeader.signature}
                      alt="Signature"
                      style={{
                        width: "180px",
                        height: "100px",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
                {formHeader?.stamp && (
                  <Box>
                    <img
                      src={formHeader.stamp}
                      alt="Stamp"
                      style={{
                        width: "250px",
                        height: "100px",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {(formHeader?.footerUrl || headerData?.footerUrl) && (
            <img
              className="discharge-footer-image"
              src={formHeader?.footerUrl || headerData?.footerUrl}
              alt=""
              style={{
                width: "100%",
                height: "70px",
                objectFit: "fill",
                display: "block",
              }}
            />
          )}
          <div className="discharge-footer-meta">
            <span>
              {referal ? "Referred By" : "Discharge By"}{" "}
              {defaultData?.doctorName} | {defaultData?.dischargeDateTime}
            </span>
            <span>
              Printed By: {loginper} | {dayjs().format("DD.MM.YYYY hh:mm A")}
            </span>
          </div>
        </div>
        )}


      </Box>
    </>
  );
};

export default DischargeReport;
